import { useEffect, useState, useRef, useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import type { ExperienceDetails } from '@atlassian/jira-business-experience-tracking/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { JwmCategoryOptionsFragmentColor } from '../../common/graphql/types.tsx';
import type { CategoryFieldConfig, CategoryState } from '../../common/types.tsx';
import {
	useCategoryOptionsCache,
	useCategoryOptionsService,
} from '../../services/category-options/index.tsx';
import { CREATE_FAILED_FLAG } from '../../services/create-category/constants.tsx';
import { useCreateCategory as useCreateCategoryService } from '../../services/create-category/index.tsx';
import { useDeleteCategory as useDeleteCategoryService } from '../../services/delete-category/index.tsx';
import { useUpdateCategory as useUpdateCategoryService } from '../../services/update-category/index.tsx';
import {
	CATEGORY_NAME_ALREADY_EXISTS_ERROR_FLAG,
	CATEGORY_NAME_ALREADY_EXISTS_ERROR_MESSAGE,
} from './constants.tsx';
import messages from './messages.tsx';
import type {
	CategoryCellStateControllerReturnType,
	CategoryFieldValueToSave,
	SelectFieldValue,
} from './types.tsx';
import { getSaveValueErrorFlag } from './utils.tsx';

export const OPTIMISTIC_UPDATE_OPTION_ID = 'OPTIMISTIC_UPDATE_OPTION_ID';
export const OPTIMISTIC_CREATE_OPTION_ID = 'OPTIMISTIC_CREATE_OPTION_ID';

/**
 * This is a hook meant to consolidate all existing category cell CRUD state management to just 1 hook
 * So the UI component only need to use this hook and nothing more
 */
export const useCategoryCellStateController = ({
	fieldValue,
	fieldType,
	issueKey,
	fieldKey,
	issueTypeId,
	createExperience,
	updateExperience,
	deleteExperience,
}: {
	fieldValue: SelectFieldValue | null;
	fieldType: string;
	issueKey: IssueKey;
	fieldKey: string;
	issueTypeId: number;
	createExperience: ExperienceDetails;
	updateExperience: ExperienceDetails;
	deleteExperience: ExperienceDetails;
}): CategoryCellStateControllerReturnType => {
	const prevFieldValueRef = useRef(fieldValue);

	const { categoryOptions } = useCategoryOptionsService();

	const [categoryState, setCategoryState] = useState<CategoryState | null>(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		fieldValue as CategoryState,
	);
	// created for functions in useCallback to read state value without dependant on it
	const categoryStateRef = useRef(categoryState);
	categoryStateRef.current = categoryState;

	const project = useProject();
	const isProjectAdmin = project.permissions.administerProject;
	const isProjectSimplified = project.isSimplified;
	const projectKey = project.key;

	const isAdmin = useIsAdmin();
	const { push } = useRouterActions();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const [{ value, error: editFieldError }, { saveValue }] = useEditField<
		CategoryFieldValueToSave | null,
		null
	>({
		fieldKey,
		issueKey,
		fieldType,
		initialValue: fieldValue
			? {
					id: fieldValue?.optionId,
					value: fieldValue?.value,
					color: fieldValue?.color,
				}
			: null,
	});

	const { createCategory } = useCreateCategoryService({ experience: createExperience });
	const { updateCategory: updateCategoryService, error: updateCategoryError } =
		useUpdateCategoryService({
			experience: updateExperience,
		});
	const { deleteCategory, error: deleteError } = useDeleteCategoryService({
		experience: deleteExperience,
	});

	const {
		addCategory: addCategoryToCache,
		updateCategory: updateCategoryInCache,
		removeCategory: removeCategoryInCache,
	} = useCategoryOptionsCache();
	const { showFlag } = useFlagService();

	useEffect(() => {
		// sync fieldValue into the local state
		const fieldValueHasChanged =
			prevFieldValueRef.current?.optionId !== fieldValue?.optionId ||
			prevFieldValueRef.current?.value !== fieldValue?.value ||
			prevFieldValueRef.current?.color !== fieldValue?.color;

		if (fieldValueHasChanged) {
			prevFieldValueRef.current = fieldValue;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			setCategoryState(fieldValue as CategoryState);
		}
	}, [categoryOptions, fieldValue, setCategoryState]);

	useEffect(() => {
		const valueHasChanged =
			categoryState?.optionId !== value?.id ||
			categoryState?.value !== value?.value ||
			categoryState?.color !== value?.color;

		if (valueHasChanged) {
			const newValue = value
				? {
						optionId: value.id,
						value: value.value,
						color: value.color,
					}
				: null;

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			setCategoryState(newValue as CategoryState);
		}
	}, [categoryState, value]);

	/**
	 * This `useEffect` is responsible for syncing localState `categoryState` with categoryOptions cache in `categoryOptions`
	 *
	 * Scenarios where this `categoryState` needs to be synced:
	 * - when a category option inside `categoryOptions` gets renamed
	 * - when a category option in `categoryOptions` is deleted
	 */
	useEffect(() => {
		const currentCategoryState = categoryStateRef.current;

		// ignore syncing with `categoryOptions`, if current cell does not have category assigned
		if (!currentCategoryState) return;

		// ignore syncing with `categoryOptions`, if the current categoryState is the result of optimistic create
		// because the optimistic created value will not exist in `categoryOptions` cache
		if (currentCategoryState.optionId === OPTIMISTIC_CREATE_OPTION_ID) return;

		const categoryOption = categoryOptions.find((o) => o.value === currentCategoryState.optionId);

		/**
		 * This code is for "when a category option in `categoryOptions` is deleted"
		 * if we cannot find the `categoryState` in the `categoryOptions` cache, it means it has been deleted
		 */
		if (!categoryOption) {
			return;
		}

		/**
		 * This code is for "when a category option inside `categoryOptions` gets updated (renamed or change color)"
		 * Updating localState `categoryState` if the categoryState.value has been renamed or color has been changed
		 */
		if (
			currentCategoryState.value !== categoryOption?.label ||
			currentCategoryState.color !== categoryOption?.color
		) {
			setCategoryState({
				...currentCategoryState,
				value: categoryOption?.label,
				color: categoryOption?.color,
			});
		}
	}, [categoryOptions, setCategoryState]);

	// created ref for functions in useCallback to read options value without dependant on it
	const categoryOptionsRef = useRef(categoryOptions);
	categoryOptionsRef.current = categoryOptions;
	const getCategoryOptionFromName = useCallback(
		(categoryName: string) =>
			categoryOptionsRef.current.find(
				(co) => co.label.toLocaleLowerCase() === categoryName.toLocaleLowerCase(),
			),
		[],
	);

	// Need to transform the value to use property key `id` instead of `optionId` when saving a category.
	// This is to avoid the issue with the saved category pointing to previously deleted category with the same name, since the BE uses `id` instead of `optionId`.
	// More details can be found on this page: https://hello.atlassian.net/wiki/spaces/JBus/pages/2986685178/Weird+behaviour+on+Category+and+Custom+Dropdown+delete+and+recover
	const getOptionIdToSave = (optionId: string) => ({ id: optionId });

	const handleSaveValueError = useCallback(
		(e: Error, id: string) => {
			// TMP error handling is located within the cell ui component
			if (!isProjectSimplified) {
				const errorFlag = getSaveValueErrorFlag(e, isAdmin || isProjectAdmin, () => {
					if (isProjectSimplified) {
						push(`/jira/core/projects/${projectKey}/settings/issuetypes`);
					} else {
						push(`/plugins/servlet/project-config/${projectKey}/fields`);
					}
				});
				showFlag(errorFlag);
			}

			fireErrorAnalytics({
				meta: {
					id,
					packageName: 'jiraBusinessCategories',
					teamName: 'jira-warepil',
				},
				error: e,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[isAdmin, isProjectAdmin, isProjectSimplified, projectKey, push, showFlag],
	);

	const checkCategoryNameAlreadyExists = useCallback(
		(categoryName: string) => {
			const trimmedCategoryName = categoryName.trim();
			if (getCategoryOptionFromName(trimmedCategoryName)) {
				return true;
			}
			return false;
		},
		[getCategoryOptionFromName],
	);

	const createAndAssignNewCategory = useCallback(
		async (categoryName: string, color: JwmCategoryOptionsFragmentColor) => {
			// check permission
			if (!isProjectAdmin) {
				return;
			}

			const trimmedCategoryName = categoryName.trim();
			if (trimmedCategoryName === '') {
				showFlag({
					type: 'error',
					title: formatMessage(messages.createdCategoryNameCantBeEmpty),
					messageId:
						'business-categories.controllers.category-cell-state-controller.show-flag.error.created-category-name-cant-be-empty',
					messageType: 'transactional',
				});
				return;
			}

			if (getCategoryOptionFromName(trimmedCategoryName)) {
				showFlag(CATEGORY_NAME_ALREADY_EXISTS_ERROR_FLAG);
				return;
			}

			// used for rollback optimistic update
			const previousCategoryState = categoryStateRef.current;

			// assign to the issue - optimistic
			setCategoryState({
				optionId: OPTIMISTIC_CREATE_OPTION_ID,
				value: trimmedCategoryName,
				color,
			});

			// Depends on the UX, we want to add the optimistic update into categoryOptions cache in this line
			// however current UX does not really require that, since the options will close instantly after creating new category, so optimistic update will never be shown to user

			let categoryMutationResult: CategoryFieldConfig | null | undefined;

			try {
				// create category in BE using mutation
				categoryMutationResult = await createCategory({
					issueTypeId,
					value: trimmedCategoryName,
					color,
				});

				// add the created mutation into options cache
				if (categoryMutationResult) {
					addCategoryToCache({
						id: categoryMutationResult.id,
						color: categoryMutationResult.color,
						option: {
							optionId: categoryMutationResult.option?.optionId,
							value: categoryMutationResult.option?.value,
							id: categoryMutationResult.option?.id,
							isDisabled: categoryMutationResult.option?.isDisabled,
						},
					});
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				const isCategoryNameAlreadyExistsError =
					e.message === CATEGORY_NAME_ALREADY_EXISTS_ERROR_MESSAGE;

				const errorFlag = isCategoryNameAlreadyExistsError
					? CATEGORY_NAME_ALREADY_EXISTS_ERROR_FLAG
					: CREATE_FAILED_FLAG;
				showFlag(errorFlag);

				// Only fire analytics for unknown errors
				if (!isCategoryNameAlreadyExistsError) {
					fireErrorAnalytics({
						meta: {
							id: 'jwmCreateCategory',
							packageName: 'jiraBusinessCategories',
							teamName: 'jira-warepil',
						},
						error: e,
						sendToPrivacyUnsafeSplunk: true,
					});
				}

				// rollback optimistic update changes when fail
				setCategoryState(previousCategoryState);
				return;
			}

			if (!categoryMutationResult) {
				fireErrorAnalytics({
					meta: {
						id: 'jwmCreateCategoryEmpty',
						packageName: 'jiraBusinessCategories',
						teamName: 'jira-warepil',
					},
					error: new Error('categoryMutationResult is empty'),
					sendToPrivacyUnsafeSplunk: true,
				});

				// rollback optimistic update changes when fail
				setCategoryState(previousCategoryState);
				return;
			}

			// then assign it after creation
			try {
				await saveValue(
					{
						...getOptionIdToSave(categoryMutationResult.option.optionId),
						value: categoryMutationResult.option.value ?? null,
						color: categoryMutationResult.color ?? null,
					},
					null,
					createAnalyticsEvent({}),
				);

				setCategoryState({
					optionId: categoryMutationResult.option.optionId,
					value: categoryMutationResult.option.value,
					color: categoryMutationResult.color,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				handleSaveValueError(e, 'jwmCreateCategoryThenAssign');
				// rollback optimistic update changes when fail
				setCategoryState(previousCategoryState);
			}
		},
		[
			isProjectAdmin,
			getCategoryOptionFromName,
			showFlag,
			formatMessage,
			createCategory,
			issueTypeId,
			addCategoryToCache,
			saveValue,
			createAnalyticsEvent,
			handleSaveValueError,
		],
	);

	const updateCategory = useCallback(
		async ({
			optionId,
			newCategoryName,
			newCategoryColor,
		}: {
			optionId: string;
			newCategoryName: string;
			newCategoryColor: JwmCategoryOptionsFragmentColor;
		}) => {
			// check permission
			if (!isProjectAdmin) {
				return;
			}

			const oldCategoryOption = categoryOptionsRef.current.find((co) => co.value === optionId);
			const trimmedNewCategoryName = newCategoryName.trim();

			if (
				trimmedNewCategoryName === oldCategoryOption?.label.trim() &&
				newCategoryColor === oldCategoryOption?.color
			) {
				return;
			}
			if (trimmedNewCategoryName === '') {
				showFlag({
					type: 'error',
					title: formatMessage(messages.renamedCategoryCantBeEmpty),
					messageId:
						'business-categories.controllers.category-cell-state-controller.show-flag.error.renamed-category-cant-be-empty',
					messageType: 'transactional',
				});
				return;
			}

			const categoryOptionWithSameName = getCategoryOptionFromName(trimmedNewCategoryName);
			if (categoryOptionWithSameName && categoryOptionWithSameName.value !== optionId) {
				showFlag(CATEGORY_NAME_ALREADY_EXISTS_ERROR_FLAG);
				return;
			}

			// update name and color optimistically in the options cache
			updateCategoryInCache({
				option: {
					optionId,
					value: trimmedNewCategoryName,
				},
				color: newCategoryColor,
			});

			const isCurrentCategoryRenamed =
				categoryStateRef.current && optionId === categoryStateRef.current.optionId;

			// also update in the issue
			if (isCurrentCategoryRenamed) {
				setCategoryState({
					optionId,
					value: trimmedNewCategoryName,
					color: newCategoryColor,
				});
			}

			let updateResponse = null;
			try {
				// send the request
				updateResponse = await updateCategoryService({
					optionId: Number.parseInt(optionId, 10),
					issueTypeId,
					value: trimmedNewCategoryName,
					color: newCategoryColor,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireErrorAnalytics({
					meta: {
						id: 'jwmCategoryUpdateCategory',
						packageName: 'jiraBusinessCategories',
						teamName: 'jira-warepil',
					},
					error: e,
					sendToPrivacyUnsafeSplunk: true,
				});
			} finally {
				// Check if update failed
				if (updateCategoryError || !updateResponse) {
					// Not firing Error flag in here, since it is already handled in service level

					fireErrorAnalytics({
						meta: {
							id: 'jwmCategoryUpdateCategoryError',
							packageName: 'jiraBusinessCategories',
							teamName: 'jira-warepil',
						},
						error: updateCategoryError
							? new Error(updateCategoryError?.message ?? 'updateCategory mutation error')
							: new Error('updateCategoryMutationResult is empty'),
						sendToPrivacyUnsafeSplunk: true,
					});

					// rollback optimistic update
					updateCategoryInCache({
						option: {
							optionId,
							value: oldCategoryOption?.label ?? '',
						},
						color: oldCategoryOption?.color ?? null,
					});

					if (isCurrentCategoryRenamed) {
						setCategoryState({
							optionId,
							value: oldCategoryOption?.label ?? '',
							color: oldCategoryOption?.color ?? null,
						});
					}
				}
			}
		},
		[
			isProjectAdmin,
			getCategoryOptionFromName,
			updateCategoryInCache,
			showFlag,
			formatMessage,
			setCategoryState,
			updateCategoryService,
			issueTypeId,
			updateCategoryError,
		],
	);

	const deleteAndUnassignCategory = useCallback(
		async (optionIdToDelete: string) => {
			// check permission
			if (!isProjectAdmin) {
				return;
			}

			const categoryOptionToDelete = categoryOptionsRef.current.find(
				(co) => co.value === optionIdToDelete,
			);

			if (!categoryOptionToDelete) {
				fireErrorAnalytics({
					meta: {
						id: 'jwmCategoryDeleteCategoryNotFound',
						packageName: 'jiraBusinessCategories',
						teamName: 'jira-warepil',
					},
					error: new Error('Cannot find categoryOption to delete'),
					sendToPrivacyUnsafeSplunk: true,
				});
				return;
			}

			// we're not doing optimistic delete because the UI will prevent users to see category again until the delete finishes, so there is no need to do rollback if delete fails too
			try {
				// delete request to BE
				const deleteSuccess = await deleteCategory({
					issueTypeId,
					optionId: Number.parseInt(optionIdToDelete, 10),
				});

				if (!deleteSuccess) {
					fireErrorAnalytics({
						meta: {
							id: 'jwmCategoryDeleteCategorySuccessFalse',
							packageName: 'jiraBusinessCategories',
							teamName: 'jira-warepil',
						},
						error: new Error(
							deleteError?.message ?? 'Category delete mutation result.success is false',
						),
						sendToPrivacyUnsafeSplunk: true,
					});
					return;
				}

				removeCategoryInCache(optionIdToDelete);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				// Not firing error flag because service layer already has it
				fireErrorAnalytics({
					meta: {
						id: 'jwmCategoryDeleteCategoryError',
						packageName: 'jiraBusinessCategories',
						teamName: 'jira-warepil',
					},
					error: e,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[deleteCategory, deleteError?.message, isProjectAdmin, issueTypeId, removeCategoryInCache],
	);

	const assignCategoryToCell = useCallback(
		async (categoryToAssign: CategoryState | null) => {
			if (categoryState?.optionId === categoryToAssign?.optionId) {
				return;
			}

			// used for rollback optimistic update
			const previousCategoryState = categoryStateRef.current;

			setCategoryState(categoryToAssign);

			try {
				await saveValue(
					categoryToAssign
						? {
								...getOptionIdToSave(categoryToAssign?.optionId),
								value: categoryToAssign?.value ?? null,
								color: categoryToAssign?.color ?? null,
							}
						: null,
					null,
					createAnalyticsEvent({}),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				handleSaveValueError(e, 'jwmCategoryAssign');

				setCategoryState(previousCategoryState);
			}
		},
		[categoryState?.optionId, saveValue, createAnalyticsEvent, handleSaveValueError],
	);

	return {
		editFieldError,
		categoryState,
		categoryOptions,
		createAndAssignNewCategory,
		checkCategoryNameAlreadyExists,
		updateCategory,
		deleteAndUnassignCategory,
		assignCategoryToCell,
	};
};
